import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  ApiDataResponse,
  HardwareConnectionType,
  HardwareDevice,
  HardwareDeviceType,
  PrintJobType,
  PrinterType,
} from '@digilize/shared/definitions/src';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class PrinterApiService {
  private readonly base = '/api/v1.0/hardware-devices';
  private readonly printersBase = '/api/v1.0/printers';

  constructor(private http: HttpClient) {}

  getPrinters(params?: Record<string, any>) {
    return this.http.get<ApiDataResponse<HardwareDevice[]>>(this.base, {
      params: { ...params },
    });
  }

  getPrinter(id: string): Observable<HardwareDevice> {
    return this.http.get<HardwareDevice>(`${this.base}/${id}`);
  }

  createPrinter(payload: CreatePrinterPayload): Observable<HardwareDevice> {
    return this.http.post<HardwareDevice>(this.base, payload);
  }

  updatePrinter(payload: UpdatePrinterPayload): Observable<HardwareDevice> {
    return this.http.put<HardwareDevice>(this.base, payload);
  }

  deletePrinter(id: string): Observable<void> {
    return this.http.delete<void>(`${this.base}/${id}`);
  }

  // Unchanged — remain on printers endpoint
  getPrinterQueue<T = any>(payload: any): Observable<T> {
    return this.http.post<T>(`${this.printersBase}/queue`, payload);
  }

  setPrinterStatus<T = any>(payload: any): Observable<T> {
    return this.http.post<T>(`${this.printersBase}/status`, payload);
  }

  printSampleReceipt = (printId: string) => this.http.post(`${this.printersBase}/${printId}/print-sample-receipt`, {});

  // PAUL-3128: URL builder kept separate from the fetch below — Task 9's dialog only needs the path to
  // pass through getFormatPreview, but keeping the builder as its own method means the service stays the
  // single place that knows this route shape.
  formatPreviewUrl = (params: FormatPreviewParams) => {
    // shop_id/point_of_sale_id are optional: with them the BE renders the sample against that tenant's
    // real header (shop name, address, phone, register name) and localizes it to the shop's country, so
    // the preview reads like the paper this register produces. Without them it falls back to a stand-in
    // cinema. Both are tenant-checked server-side, so passing a shop the admin has no authority over is
    // refused rather than rendered.
    const context = [
      params.shopId ? `&shop_id=${encodeURIComponent(params.shopId)}` : '',
      params.pointOfSaleId ? `&point_of_sale_id=${encodeURIComponent(params.pointOfSaleId)}` : '',
    ].join('');

    return `/api/v1.0/printer-formats/${params.jobType}/preview?printer_type=${
      params.printerType
    }&format=${encodeURIComponent(params.format)}${context}`;
  };

  /* Blob GET, not a bare img/iframe src: this endpoint is JWT-authenticated and staff-only, and the bearer
     token is only attached by ApiInterceptor to requests that go through HttpClient (see the
     seatQrCodePngUrl comment in pos-api.service.ts for the same reasoning in reverse — that endpoint is
     anonymous, so a plain URL works there). A native <img>/<iframe> load bypasses the interceptor
     entirely, so it would hit this endpoint with no Authorization header and get a 401/403. Same shape as
     POSApiService.getSeatQrCodesPdf: Task 9's dialog builds an object URL from the blob and binds that to
     img/iframe src. Epson formats resolve to image/png; Boca formats resolve to the layout view's HTML
     (text/html) meant for an iframe, not a PDF, despite "PDF view" language elsewhere.
  */
  getFormatPreview = (params: FormatPreviewParams) =>
    this.http.get(this.formatPreviewUrl(params), { responseType: 'blob' });
}

export interface FormatPreviewParams {
  jobType: PrintJobType;
  printerType: PrinterType;
  format: string;
  /** The shop being configured — its real header replaces the stand-in cinema in the sample. */
  shopId?: string;
  /** Only the financial report names a register; a shop cannot supply one. */
  pointOfSaleId?: string;
}

export interface CreatePrinterPayload {
  name: string;
  // Optional, free text, not unique. "" is the same as omitting it on create.
  tag?: string | null;
  // Epson-only queue identity (rev 3 ruling R2) — omitted entirely for Boca devices, which have
  // no queue/ePOS protocol and therefore no username/password to authenticate with.
  username?: string;
  password?: string;
  shop_id: string;
  type: HardwareDeviceType;
  connection_type?: HardwareConnectionType | null;
  connected_printer_device_id?: string | null;
  usb_config_json?: string | null;
  point_of_sale_id?: string | null;
  printer_type?: PrinterType | null;
  template_key?: string | null;
  receipt_format_version?: number | null;
  ticket_template_key?: string | null;
  // Same "" = clear / null-or-absent = keep-or-set mechanics as template_key.
  kitchen_receipt_template_key?: string | null;
  // Same "" = clear / null-or-absent = keep-or-set mechanics as template_key.
  financial_report_template_key?: string | null;
}

export interface UpdatePrinterPayload {
  id: string;
  name: string;
  // Same "" = clear / omitted-or-null = keep-stored-value mechanics as template_key.
  tag?: string | null;
  // Epson-only queue identity (rev 3 ruling R2) — omitted entirely for Boca devices.
  username?: string;
  password?: string | null;
  shop_id?: string;
  type: HardwareDeviceType;
  connection_type?: HardwareConnectionType | null;
  connected_printer_device_id?: string | null;
  usb_config_json?: string | null;
  printer_type?: PrinterType | null;
  template_key?: string | null;
  // -1 is the rev 3 "clear to not-supported" sentinel on edit (null on edit means "leave the
  // stored value alone"); null is a real value only on create. See add-edit-printer.component.ts.
  receipt_format_version?: number | null;
  // Same "" = clear / non-null value = keep-or-set mechanics as template_key.
  ticket_template_key?: string | null;
  // Same "" = clear / omitted-or-null = keep-stored-value mechanics as template_key.
  kitchen_receipt_template_key?: string | null;
  // Same "" = clear / omitted-or-null = keep-stored-value mechanics as template_key.
  financial_report_template_key?: string | null;
}
